import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bell, Send, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { format } from "date-fns";

const notificationSchema = z.object({
  message: z.string()
    .min(10, "Mensagem deve ter no mínimo 10 caracteres")
    .max(500, "Mensagem muito longa (máximo 500 caracteres)"),
  type: z.enum([
    "system_update",
    "new_feature", 
    "warning",
    "maintenance",
    "promotion",
    "urgent"
  ]),
  category: z.enum([
    "geral",
    "urgente",
    "promocao",
    "sistema"
  ]),
  target: z.enum(["all", "user", "plan"]),
  targetValue: z.string().optional()
}).refine(data => {
  if (data.target === "user" || data.target === "plan") {
    return !!data.targetValue && data.targetValue.trim().length > 0;
  }
  return true;
}, {
  message: "Este campo é obrigatório para o tipo de destinatário selecionado",
  path: ["targetValue"]
});

type NotificationFormData = z.infer<typeof notificationSchema>;

interface NotificationHistory {
  id: string;
  message: string;
  type: string;
  category: string;
  created_at: string;
  recipient_count?: number;
}

export const AdminNotifications = () => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const { toast } = useToast();
  const formRef = useRef<HTMLDivElement>(null);

  const form = useForm<NotificationFormData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      message: "",
      type: "system_update",
      category: "geral",
      target: "all",
      targetValue: ""
    }
  });

  const watchedValues = form.watch();

  useEffect(() => {
    loadNotificationHistory();
  }, []);

  const loadNotificationHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id, message, type, category, created_at')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      // Group by message to count recipients
      const grouped = data?.reduce((acc: any[], curr) => {
        const existing = acc.find(item => 
          item.message === curr.message && 
          item.type === curr.type &&
          Math.abs(new Date(item.created_at).getTime() - new Date(curr.created_at).getTime()) < 5000
        );
        
        if (existing) {
          existing.recipient_count = (existing.recipient_count || 1) + 1;
        } else {
          acc.push({ ...curr, recipient_count: 1 });
        }
        return acc;
      }, []);

      setHistory(grouped || []);
    } catch (error) {
      console.error("Error loading notification history:", error);
    }
  };

  const sendToAllUsers = async (data: NotificationFormData) => {
    const { data: users, error: fetchError } = await supabase
      .from('profiles')
      .select('id');
    
    if (fetchError) throw fetchError;
    if (!users || users.length === 0) {
      toast({
        title: "Nenhum usuário encontrado",
        description: "Não há usuários cadastrados no sistema",
        variant: "destructive"
      });
      return;
    }

    const notifications = users.map(user => ({
      user_id: user.id,
      message: data.message,
      type: data.type,
      category: data.category,
      is_read: false
    }));

    const { error: insertError } = await supabase
      .from('notifications')
      .insert(notifications);

    if (insertError) throw insertError;

    toast({
      title: "✅ Notificação enviada com sucesso!",
      description: `Enviada para ${users.length} usuário(s)`,
    });
  };

  const sendToUser = async (data: NotificationFormData) => {
    const email = data.targetValue?.trim().toLowerCase();
    
    const { data: user, error: fetchError } = await supabase
      .from('profiles')
      .select('id, email')
      .eq('email', email)
      .single();
    
    if (fetchError || !user) {
      toast({
        title: "Usuário não encontrado",
        description: `Nenhum usuário com email "${email}" foi encontrado`,
        variant: "destructive"
      });
      return;
    }

    const { error: insertError } = await supabase
      .from('notifications')
      .insert({
        user_id: user.id,
        message: data.message,
        type: data.type,
        category: data.category,
        is_read: false
      });

    if (insertError) throw insertError;

    toast({
      title: "✅ Notificação enviada!",
      description: `Enviada para ${user.email}`,
    });
  };

  const sendToPlan = async (data: NotificationFormData) => {
    const planName = data.targetValue?.toLowerCase();
    
    const { data: users, error: fetchError } = await supabase
      .from('profiles')
      .select('id')
      .eq('plan', planName);
    
    if (fetchError) throw fetchError;
    
    if (!users || users.length === 0) {
      toast({
        title: "Nenhum usuário encontrado",
        description: `Nenhum usuário com o plano "${planName}" foi encontrado`,
        variant: "destructive"
      });
      return;
    }

    const notifications = users.map(user => ({
      user_id: user.id,
      message: data.message,
      type: data.type,
      category: data.category,
      is_read: false
    }));

    const { error: insertError } = await supabase
      .from('notifications')
      .insert(notifications);

    if (insertError) throw insertError;

    toast({
      title: "✅ Notificação enviada com sucesso!",
      description: `Enviada para ${users.length} usuário(s) do plano ${planName}`,
    });
  };

  const onSubmit = async (data: NotificationFormData) => {
    setLoading(true);
    try {
      if (data.target === "all") {
        await sendToAllUsers(data);
      } else if (data.target === "user") {
        await sendToUser(data);
      } else if (data.target === "plan") {
        await sendToPlan(data);
      }

      form.reset();
      await loadNotificationHistory();
    } catch (error: any) {
      console.error("Error sending notification:", error);
      toast({
        title: "Erro ao enviar notificação",
        description: error.message || "Tente novamente",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getBadgeVariant = (type: string) => {
    const variants: Record<string, "default" | "destructive" | "secondary"> = {
      system_update: "default",
      new_feature: "default",
      warning: "destructive",
      maintenance: "secondary",
      promotion: "default",
      urgent: "destructive"
    };
    return variants[type] || "default";
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      system_update: "Atualização",
      new_feature: "Novo Recurso",
      warning: "Aviso",
      maintenance: "Manutenção",
      promotion: "Promoção",
      urgent: "Urgente"
    };
    return labels[type] || type;
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const applyPreset = (type: NotificationFormData["type"], category: NotificationFormData["category"]) => {
    form.setValue("type", type);
    form.setValue("category", category);
    scrollToForm();
  };

  return (
    <div className="space-y-6">
      {/* Preset Buttons - Mantidos do design original */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Atalhos Rápidos
          </CardTitle>
          <CardDescription>
            Clique em um atalho para preencher automaticamente o tipo de notificação
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Button 
                onClick={() => applyPreset("system_update", "geral")}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
              >
                📢 Criar Aviso Geral
              </Button>
              <Button 
                onClick={() => applyPreset("maintenance", "sistema")}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              >
                ⚡ Aviso de Manutenção
              </Button>
            </div>
            <div className="space-y-4">
              <Button 
                onClick={() => applyPreset("promotion", "promocao")}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
              >
                🎯 Aviso de Promoção
              </Button>
              <Button 
                onClick={() => applyPreset("urgent", "urgente")}
                className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600"
              >
                🚨 Aviso Urgente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form and Preview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" ref={formRef}>
        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Criar Nova Notificação</CardTitle>
            <CardDescription>Preencha os campos abaixo para enviar uma notificação</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="message">Mensagem *</Label>
                <Textarea
                  id="message"
                  placeholder="Digite a mensagem da notificação..."
                  {...form.register("message")}
                  rows={4}
                />
                {form.formState.errors.message && (
                  <p className="text-sm text-destructive">{form.formState.errors.message.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Tipo *</Label>
                  <Select
                    value={watchedValues.type}
                    onValueChange={(value) => form.setValue("type", value as NotificationFormData["type"])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system_update">Atualização do Sistema</SelectItem>
                      <SelectItem value="new_feature">Novo Recurso</SelectItem>
                      <SelectItem value="warning">Aviso</SelectItem>
                      <SelectItem value="maintenance">Manutenção</SelectItem>
                      <SelectItem value="promotion">Promoção</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Categoria *</Label>
                  <Select
                    value={watchedValues.category}
                    onValueChange={(value) => form.setValue("category", value as NotificationFormData["category"])}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="geral">Geral</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                      <SelectItem value="promocao">Promoção</SelectItem>
                      <SelectItem value="sistema">Sistema</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target">Destinatários *</Label>
                <Select
                  value={watchedValues.target}
                  onValueChange={(value) => {
                    form.setValue("target", value as NotificationFormData["target"]);
                    form.setValue("targetValue", "");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Usuários</SelectItem>
                    <SelectItem value="user">Usuário Específico</SelectItem>
                    <SelectItem value="plan">Por Plano</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {watchedValues.target === "user" && (
                <div className="space-y-2">
                  <Label htmlFor="targetValue">Email do Usuário *</Label>
                  <Input
                    id="targetValue"
                    type="email"
                    placeholder="usuario@exemplo.com"
                    {...form.register("targetValue")}
                  />
                  {form.formState.errors.targetValue && (
                    <p className="text-sm text-destructive">{form.formState.errors.targetValue.message}</p>
                  )}
                </div>
              )}

              {watchedValues.target === "plan" && (
                <div className="space-y-2">
                  <Label htmlFor="targetValue">Plano *</Label>
                  <Select
                    value={watchedValues.targetValue || ""}
                    onValueChange={(value) => form.setValue("targetValue", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o plano" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="basico">Básico</SelectItem>
                      <SelectItem value="ouro">Ouro</SelectItem>
                      <SelectItem value="platina">Platina</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.formState.errors.targetValue && (
                    <p className="text-sm text-destructive">{form.formState.errors.targetValue.message}</p>
                  )}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Notificação
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Preview and History Column */}
        <div className="space-y-6">
          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview da Notificação</CardTitle>
              <CardDescription>Veja como sua notificação aparecerá</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 border rounded-lg bg-muted/50">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 mt-1 text-primary" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm uppercase tracking-wide">
                        {watchedValues.category}
                      </p>
                      <Badge variant={getBadgeVariant(watchedValues.type)}>
                        {getTypeLabel(watchedValues.type)}
                      </Badge>
                    </div>
                    <p className="text-sm">
                      {watchedValues.message || "Digite uma mensagem para visualizar..."}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>Agora</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Histórico de Notificações</CardTitle>
              <CardDescription>Últimas 20 notificações enviadas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Mensagem</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Enviadas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          <div className="py-8">
                            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Nenhuma notificação enviada ainda</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      history.map((notif) => (
                        <TableRow key={notif.id}>
                          <TableCell className="text-xs">
                            {format(new Date(notif.created_at), "dd/MM HH:mm")}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm">
                            {notif.message}
                          </TableCell>
                          <TableCell>
                            <Badge variant={getBadgeVariant(notif.type)} className="text-xs">
                              {getTypeLabel(notif.type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span className="text-xs font-medium">{notif.recipient_count}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
